import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listStaff, updateStaffBusy, updateStaffCompact, updateStaffResident, getPeriodTimes, listTimetableWeeks, setWeekBusyOpen, DAYS, TEACHER_DEPARTMENT } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const fmtDate = (d) => {
  if (!d) return "";
  const p = String(d).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : d;
};

const shortDay = (d) => String(d).replace("Thứ ", "T");

// Suy ra buổi + tiết toàn cục từ cấu hình giờ học.
function buildSessions(pt) {
  let cur = 0;
  return (pt && pt.sessions ? pt.sessions : []).map((s) => {
    const periods = (s.periods || []).map((time, i) => ({ global: cur + i, label: `Tiết ${i + 1}`, time }));
    cur += (s.periods || []).length;
    return { key: s.key, label: s.label, periods };
  });
}

const HappySchoolTeacherBusy = ({ weekId: initialWeekId }) => {
  const schoolId = getActiveSchoolId();
  const [weeks, setWeeks] = useState([]);
  const [weekId, setWeekId] = useState("");
  const [weeksLoading, setWeeksLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [mode, setMode] = useState("all"); // "one" | "all" — mặc định xem toàn trường (chỉ đọc)
  const [drafts, setDrafts] = useState({}); // teacherId -> string[] (thay đổi chưa lưu)
  const [saving, setSaving] = useState(false);
  const [busyToggling, setBusyToggling] = useState(false);

  // Lịch bận luôn gắn với 1 tuần TKB — tải danh sách tuần trước (mọi tuần,
  // không chỉ tuần đang mở khai báo tự phục vụ — admin sửa được cho mọi tuần).
  useEffect(() => {
    if (!schoolId) return;
    setWeeksLoading(true);
    listTimetableWeeks(schoolId)
      .then((list) => {
        const sorted = (list || []).slice().sort((a, b) => String(b.startDate).localeCompare(String(a.startDate)));
        setWeeks(sorted);
        setWeekId((cur) => {
          if (cur && sorted.find((w) => w.id === cur)) return cur;
          if (initialWeekId && sorted.find((w) => w.id === initialWeekId)) return initialWeekId;
          return sorted[0] ? sorted[0].id : "";
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setWeeksLoading(false));
  }, [schoolId]);

  const reload = () => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    Promise.all([listStaff(schoolId, { department: TEACHER_DEPARTMENT, weekId }), getPeriodTimes(schoolId)])
      .then(([list, pt]) => {
        const sorted = list.slice().sort((a, b) => a.name.localeCompare(b.name, "vi"));
        setTeachers(sorted);
        setSessions(buildSessions(pt));
        if (sorted.length > 0 && !sorted.find((t) => t.id === selectedId)) setSelectedId(sorted[0].id);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [schoolId, weekId]);

  const currentWeek = weeks.find((w) => w.id === weekId);
  const toggleBusyOpen = async () => {
    if (!currentWeek) return;
    const next = !currentWeek.busyOpen;
    setBusyToggling(true);
    setError("");
    try {
      await setWeekBusyOpen(schoolId, currentWeek.id, next);
      setWeeks((list) => list.map((w) => (w.id === currentWeek.id ? { ...w, busyOpen: next } : w)));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyToggling(false);
    }
  };

  if (!schoolId) {
    return (
      <div style={{ padding: 16 }}>
        Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link> trước.
      </div>
    );
  }

  const teacher = teachers.find((t) => t.id === selectedId);
  // busy hiện dùng = bản nháp nếu đang chỉnh, ngược lại lấy từ server.
  const busyOf = (t) => new Set(t ? (drafts[t.id] || t.busy || []) : []);
  const isDirty = (t) => t && Object.prototype.hasOwnProperty.call(drafts, t.id);
  const dirtyIds = Object.keys(drafts);

  // Tuần đã ĐÓNG khai báo -> màn quản lý này cũng chỉ xem, không cho bấm sửa
  // nữa (trước đây chỉ khoá được ở màn GV tự khai báo, còn màn admin thì lúc
  // nào cũng bấm sửa được bất kể trạng thái đóng/mở).
  const busyReadOnly = !!currentWeek && currentWeek.busyOpen !== true;

  const setDraft = (t, set) => setDrafts((d) => ({ ...d, [t.id]: Array.from(set) }));
  const togglePeriod = (t, day, global) => {
    if (busyReadOnly) return;
    const set = busyOf(t);
    const key = `${day}_${global}`;
    if (set.has(key)) set.delete(key);
    else set.add(key);
    setDraft(t, set);
  };
  const toggleSession = (t, day, ses) => {
    if (busyReadOnly) return;
    const set = busyOf(t);
    const keys = ses.periods.map((p) => `${day}_${p.global}`);
    const allBusy = keys.every((k) => set.has(k));
    keys.forEach((k) => (allBusy ? set.delete(k) : set.add(k)));
    setDraft(t, set);
  };
  const saveDrafts = async () => {
    setSaving(true);
    setError("");
    try {
      for (const id of dirtyIds) {
        await updateStaffBusy(schoolId, id, drafts[id], weekId);
      }
      setDrafts({});
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const cancelDrafts = () => setDrafts({});

  const [compactSaving, setCompactSaving] = useState(false);
  const toggleCompact = async (t) => {
    if (!t) return;
    const next = !t.compact;
    setCompactSaving(true);
    setError("");
    try {
      await updateStaffCompact(schoolId, t.id, next);
      setTeachers((list) => list.map((x) => (x.id === t.id ? { ...x, compact: next } : x)));
    } catch (err) {
      setError(err.message);
    } finally {
      setCompactSaving(false);
    }
  };
  const [residentSaving, setResidentSaving] = useState(false);
  const toggleResident = async (t) => {
    if (!t) return;
    const next = !t.resident;
    setResidentSaving(true);
    setError("");
    try {
      await updateStaffResident(schoolId, t.id, next);
      setTeachers((list) => list.map((x) => (x.id === t.id ? { ...x, resident: next } : x)));
    } catch (err) {
      setError(err.message);
    } finally {
      setResidentSaving(false);
    }
  };
  const sessionBusyCount = (t, day, ses) => {
    const set = busyOf(t);
    return ses.periods.filter((p) => set.has(`${day}_${p.global}`)).length;
  };

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Đánh dấu buổi/tiết giáo viên KHÔNG thể dạy — dữ liệu này dùng để xếp thời khoá biểu tự động. Lịch bận gắn với <b>đúng 1 tuần TKB</b> (không còn lịch bận chung chung nữa). Bấm ô "cả buổi" để tô/bỏ nhanh cả buổi.
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Tuần TKB:</span>
        {weeksLoading ? <span style={{ fontSize: 13, color: "#6b7280" }}>Đang tải...</span> : weeks.length === 0 ? (
          <span style={{ fontSize: 13, color: "#b45309" }}>Chưa có tuần TKB nào — tạo tuần ở mục "Quản lý TKB" trước.</span>
        ) : (
          <select value={weekId} onChange={(e) => { setWeekId(e.target.value); setDrafts({}); }} style={{ padding: 8, minWidth: 260 }}>
            {weeks.map((w) => (
              <option key={w.id} value={w.id}>{w.name ? `${w.name} — ` : ""}{fmtDate(w.startDate)} → {fmtDate(w.endDate)}{w.busyOpen ? " • đang mở tự khai báo" : ""}</option>
            ))}
          </select>
        )}
        {currentWeek && (
          <>
            <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: currentWeek.busyOpen ? "#dcfce7" : "#f3f4f6", color: currentWeek.busyOpen ? "#166534" : "#6b7280" }}>
              Khai báo bận: {currentWeek.busyOpen ? "ĐANG MỞ" : "ĐÃ ĐÓNG"}
            </span>
            <button type="button" onClick={toggleBusyOpen} disabled={busyToggling}
              title={currentWeek.busyOpen ? "Đóng lại: thầy cô chỉ xem, không tự khai báo/sửa được nữa" : "Mở cho thầy cô tự khai báo lịch bận tuần này"}
              style={currentWeek.busyOpen ? closeBtnStyle : openBtnStyle}>
              {busyToggling ? "..." : currentWeek.busyOpen ? "🔒 Đóng khai báo" : "🔓 Mở khai báo cho GV"}
            </button>
          </>
        )}
      </div>

      {!weekId ? null : (
      <>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" onClick={() => setMode("all")} style={mode === "all" ? tabActive : tab}>Toàn trường</button>
        <button type="button" onClick={() => setMode("one")} style={mode === "one" ? tabActive : tab}>Theo giáo viên</button>
        {dirtyIds.length > 0 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
            <span style={{ fontSize: 13, color: "#b45309", fontWeight: 600 }}>
              ● {dirtyIds.length} giáo viên chưa lưu
            </span>
            <button type="button" onClick={cancelDrafts} disabled={saving} style={tab}>Huỷ</button>
            <button type="button" onClick={saveDrafts} disabled={saving} style={saveBtn}>
              {saving ? "Đang lưu..." : "💾 Lưu thay đổi"}
            </button>
          </div>
        )}
      </div>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {loading ? (
        <p>Đang tải...</p>
      ) : teachers.length === 0 ? (
        <p style={{ color: "#6b7280" }}>
          Chưa có giáo viên nào. Vào <Link to="/happyschool/teachers">Danh sách giáo viên</Link> để thêm trước.
        </p>
      ) : mode === "one" ? (
        <>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ padding: 8, minWidth: 240 }}>
              {teachers.map((t) => (<option key={t.id} value={t.id}>{drafts[t.id] ? "● " : ""}{t.name}</option>))}
            </select>
            {isDirty(teacher) && <span style={{ fontSize: 12, color: "#b45309" }}>Có thay đổi chưa lưu — bấm "Lưu thay đổi".</span>}
            {busyReadOnly && <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>🔒 Tuần này đã đóng khai báo — chỉ xem, không sửa được</span>}
            {teacher && (
              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", background: teacher.compact ? "#ecfdf5" : "transparent", border: "1px solid #d1d5db", borderRadius: 6, padding: "4px 10px" }}
                title="Dồn tất cả tiết của GV này vào ít buổi/ngày nhất khi xếp lịch — dùng cho GV nhà xa.">
                <input type="checkbox" checked={!!teacher.compact} disabled={compactSaving} onChange={() => toggleCompact(teacher)} />
                🏠 Gom lịch (nhà xa){compactSaving ? " …" : ""}
              </label>
            )}
            {teacher && (
              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", background: teacher.resident ? "#ecfdf5" : "transparent", border: "1px solid #d1d5db", borderRadius: 6, padding: "4px 10px" }}
                title="GV ở gần trường/nội trú — không tốn công đi lại nên KHÔNG bị phạt khi lên trường chỉ dạy đúng 1 tiết (bình thường GV phải dạy ≥2 tiết/lần lên trường mới không bị phạt).">
                <input type="checkbox" checked={!!teacher.resident} disabled={residentSaving} onChange={() => toggleResident(teacher)} />
                🛏️ Nội trú/gần trường{residentSaving ? " …" : ""}
              </label>
            )}
          </div>
          {teacher && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Buổi / Tiết</th>
                    {DAYS.map((d) => (<th key={d} style={thStyle}>{d}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((ses) => (
                    <React.Fragment key={ses.key}>
                      <tr>
                        <td style={{ ...thStyle, textAlign: "left" }}>{ses.label} — cả buổi</td>
                        {DAYS.map((_, day) => {
                          const cnt = sessionBusyCount(teacher, day, ses);
                          const full = cnt === ses.periods.length && cnt > 0;
                          return (
                            <td key={day} onClick={() => toggleSession(teacher, day, ses)}
                              style={{ ...tdStyle, cursor: busyReadOnly ? "default" : "pointer", fontWeight: 700, background: full ? "#fca5a5" : cnt > 0 ? "#fde68a" : "#e5f5ea" }}
                              title={busyReadOnly ? "Chỉ xem — tuần đã đóng khai báo" : "Bấm để tô/bỏ cả buổi"}>
                              {full ? "Bận" : cnt > 0 ? `${cnt}/${ses.periods.length}` : ""}
                            </td>
                          );
                        })}
                      </tr>
                      {ses.periods.map((p) => (
                        <tr key={p.global}>
                          <td style={{ ...tdStyle, textAlign: "left", color: "#6b7280", paddingLeft: 22 }}>{ses.label[0]}·{p.label} <span style={{ fontSize: 11 }}>{p.time}</span></td>
                          {DAYS.map((_, day) => {
                            const isBusy = busyOf(teacher).has(`${day}_${p.global}`);
                            return (
                              <td key={day} onClick={() => togglePeriod(teacher, day, p.global)}
                                style={{ ...tdStyle, cursor: busyReadOnly ? "default" : "pointer", background: isBusy ? "#fecaca" : "#f0fdf4" }}
                                title={busyReadOnly ? "Chỉ xem — tuần đã đóng khai báo" : isBusy ? "Bận (bấm bỏ)" : "Rảnh (bấm đánh dấu bận)"}>
                                {isBusy ? "Bận" : ""}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div style={{ overflow: "auto", maxHeight: "70vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, position: "sticky", top: 0, left: 0, zIndex: 3, textAlign: "left" }}>Giáo viên</th>
                {DAYS.map((d) =>
                  sessions.map((ses) => (
                    <th key={d + ses.key} style={{ ...thStyle, position: "sticky", top: 0, zIndex: 2, whiteSpace: "nowrap" }}>{shortDay(d)} {ses.label}</th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {teachers.filter((t) => busyOf(t).size > 0 || drafts[t.id]).map((t) => (
                <tr key={t.id}>
                  <td style={{ ...thStyle, position: "sticky", left: 0, zIndex: 1, textAlign: "left", whiteSpace: "nowrap" }}>{t.name}</td>
                  {DAYS.map((_, day) =>
                    sessions.map((ses) => {
                      const cnt = sessionBusyCount(t, day, ses);
                      const full = cnt === ses.periods.length && cnt > 0;
                      return (
                        <td key={day + ses.key}
                          style={{ ...tdStyle, background: full ? "#fca5a5" : cnt > 0 ? "#fde68a" : "#f0fdf4" }}
                          title={`${t.name} — ${shortDay(DAYS[day])} ${ses.label}: ${cnt}/${ses.periods.length} tiết bận`}>
                          {full ? "Bận" : cnt > 0 ? `${cnt}/${ses.periods.length}` : ""}
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 12, color: "#6b7280", padding: "6px 10px" }}>Chỉ xem — ô hiện số tiết bận/tổng của buổi. Muốn sửa thì chuyển sang "Theo giáo viên".</p>
        </div>
      )}
      </>
      )}
    </div>
  );
};

const tab = { padding: "6px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontSize: 13 };
const tabActive = { ...tab, background: "#0ea5a3", color: "#fff", borderColor: "#0ea5a3", fontWeight: 700 };
const saveBtn = { padding: "6px 16px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 700 };
const closeBtnStyle = { padding: "6px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 700 };
const openBtnStyle = { padding: "6px 14px", background: "#059669", color: "#fff", border: "none", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 700 };
const thStyle = { border: "1px solid #e5e7eb", padding: "6px 10px", background: "#f9fafb", whiteSpace: "nowrap" };
const tdStyle = { border: "1px solid #e5e7eb", padding: "6px 10px", textAlign: "center" };

export default HappySchoolTeacherBusy;
